import { Injectable } from '@angular/core'
import { JwtHelperService } from '@auth0/angular-jwt'

export interface AdminPrefs {
  /** NavLeaf ids, in pin order. */
  favorites: string[]
  /** NavLeaf ids, most recent first. */
  recents: string[]
  /**
   * NavGroup ids the admin has expanded. Stored as open rather than collapsed so
   * the default — nothing listed — is the compact menu everything else is built
   * around: sections shut, six rows visible.
   */
  openGroups: string[]
  sidebarCollapsed: boolean
}

/**
 * The only localStorage keys that belong to the session.
 *
 * Logout used to call `localStorage.clear()`, which would wipe preferences along
 * with the token. It now removes exactly these, so anything else stored here —
 * preferences included — survives signing out.
 */
export const AUTH_STORAGE_KEYS = ['token', 'roles', 'accessControls']

const KEY_PREFIX = 'tktah.admin.prefs'
const MAX_RECENTS = 5

const DEFAULTS: AdminPrefs = {
  favorites: [],
  recents: [],
  openGroups: [],
  sidebarCollapsed: false,
}

/**
 * Per-admin UI preferences, kept in localStorage and scoped by user id so two
 * admins sharing a browser profile don't inherit each other's pinned pages.
 */
@Injectable({ providedIn: 'root' })
export class AdminPrefsService {
  private jwtHelper = new JwtHelperService()
  private cache: AdminPrefs | null = null
  private cachedKey: string | null = null

  get prefs(): AdminPrefs {
    const key = this.storageKey()
    if (!this.cache || this.cachedKey !== key) {
      this.cache = this.read(key)
      this.cachedKey = key
    }
    return this.cache
  }

  isFavorite(leafId: string): boolean {
    return this.prefs.favorites.includes(leafId)
  }

  toggleFavorite(leafId: string): void {
    const favorites = this.isFavorite(leafId)
      ? this.prefs.favorites.filter((id) => id !== leafId)
      : [...this.prefs.favorites, leafId]
    this.patch({ favorites })
  }

  recordVisit(leafId: string): void {
    const recents = [leafId, ...this.prefs.recents.filter((id) => id !== leafId)].slice(
      0,
      MAX_RECENTS,
    )
    this.patch({ recents })
  }

  isGroupOpen(groupId: string): boolean {
    return this.prefs.openGroups.includes(groupId)
  }

  setGroupOpen(groupId: string, open: boolean): void {
    const openGroups = open
      ? [...new Set([...this.prefs.openGroups, groupId])]
      : this.prefs.openGroups.filter((id) => id !== groupId)
    this.patch({ openGroups })
  }

  setSidebarCollapsed(sidebarCollapsed: boolean): void {
    this.patch({ sidebarCollapsed })
  }

  /** Drop the in-memory copy so the next read re-scopes to whoever just logged in. */
  reset(): void {
    this.cache = null
    this.cachedKey = null
  }

  private patch(change: Partial<AdminPrefs>): void {
    const next = { ...this.prefs, ...change }
    this.cache = next
    const key = this.storageKey()
    this.cachedKey = key
    try {
      localStorage.setItem(key, JSON.stringify(next))
    } catch {
      // Quota exceeded or storage disabled — preferences are a convenience, so
      // keep the in-memory copy and carry on rather than breaking navigation.
    }
  }

  private read(key: string): AdminPrefs {
    try {
      const raw = localStorage.getItem(key)
      if (!raw) return { ...DEFAULTS }
      const parsed = JSON.parse(raw)
      // Merge over defaults: a payload written by an older build may be missing
      // fields added since, and a half-populated object would break `.includes`.
      return {
        favorites: Array.isArray(parsed?.favorites) ? parsed.favorites : [],
        recents: Array.isArray(parsed?.recents) ? parsed.recents : [],
        openGroups: Array.isArray(parsed?.openGroups) ? parsed.openGroups : [],
        sidebarCollapsed: !!parsed?.sidebarCollapsed,
      }
    } catch {
      return { ...DEFAULTS }
    }
  }

  /**
   * `_id` is the id claim the backend signs into the access token
   * (see helpers/acl/is.allowed.ts, which reads `user._id` off the verified JWT).
   * Falls back to an unscoped key rather than dropping preferences entirely.
   */
  private storageKey(): string {
    try {
      const token = localStorage.getItem('token')
      const userId = token ? this.jwtHelper.decodeToken(token)?._id : null
      return userId ? `${KEY_PREFIX}.${userId}` : `${KEY_PREFIX}.anonymous`
    } catch {
      return `${KEY_PREFIX}.anonymous`
    }
  }
}
